import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Gavi } from '#components/mobile/travesia/Gavi';
import { Marca } from '#components/mobile/travesia/pasos/comun';
import { Boton } from '#components/mobile/ui/Boton';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import { color, texto } from '#components/mobile/ui/tokens';

export type ComercioOfrecido = {
  payee: string;
  nombre: string;
  nombreCategoria: string;
  /** Cuántos movimientos se colocarían. */
  n: number;
};

type MismoComercioProps = {
  comercios: ComercioOfrecido[];
  colocando?: boolean;
  /** Con los comercios marcados (todos, si solo hay uno). */
  onColocar: (payees: string[]) => void;
  onNo: () => void;
};

/**
 * Tarjeta de Gavi «¿los pongo ahí?» para los comercios que siempre van a la
 * misma categoría. Con uno, pregunta por él; con varios, deja desmarcar los
 * que no quieras. Se usa al terminar de importar un extracto y la puede usar
 * la travesía para ordenar la historia.
 */
export function MismoComercio({
  comercios,
  colocando = false,
  onColocar,
  onNo,
}: MismoComercioProps) {
  const { t } = useTranslation();
  const [desmarcados, setDesmarcados] = useState<Set<string>>(new Set());

  if (comercios.length === 0) {
    return null;
  }
  const uno = comercios.length === 1 ? comercios[0] : null;
  const marcados = comercios.filter(c => !desmarcados.has(c.payee));
  const total = marcados.reduce((s, c) => s + c.n, 0);

  const alternar = (payee: string) =>
    setDesmarcados(d => {
      const r = new Set(d);
      if (!r.delete(payee)) {
        r.add(payee);
      }
      return r;
    });

  return (
    <Tarjeta style={{ gap: 12 }} data-testid="mismo-comercio">
      <View style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-start' }}>
        <Gavi estado="ladea" ancho={40} style={{ flexShrink: 0 }} />
        <Text style={{ ...texto.cuerpo, flex: 1, color: color.fg }}>
          {uno
            ? t(
                'There are {{count}} from {{payee}} without a category. You always put them in {{category}}. Shall I put them there?',
                {
                  count: uno.n,
                  payee: uno.nombre,
                  category: uno.nombreCategoria,
                },
              )
            : t('You always put these in the same place. Shall I sort them?')}
        </Text>
      </View>

      {!uno && (
        <View>
          {comercios.map(c => {
            const marcado = !desmarcados.has(c.payee);
            return (
              <button
                type="button"
                key={c.payee}
                role="checkbox"
                aria-checked={marcado}
                data-testid={`mismo-comercio-${c.payee}`}
                onClick={() => alternar(c.payee)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  minHeight: 52,
                  padding: '6px 0',
                  width: '100%',
                  textAlign: 'left',
                  boxShadow: `inset 0 1px 0 ${color.line}`,
                  background: 'none',
                  border: 0,
                  color: color.fg,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <Marca marcada={marcado} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', ...texto.cuerpo }}>
                    {c.nombre}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      ...texto.secundario,
                      color: color.fg2,
                    }}
                  >
                    {t('in {{category}}', { category: c.nombreCategoria })}
                  </span>
                </span>
                <span
                  style={{
                    ...texto.secundario,
                    color: color.fg2,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {c.n}
                </span>
              </button>
            );
          })}
        </View>
      )}

      <Boton
        bloque
        isDisabled={colocando || total === 0}
        onPress={() => onColocar(marcados.map(c => c.payee))}
        data-testid="mismo-comercio-si"
      >
        {uno
          ? t('Yes, in {{category}}', { category: uno.nombreCategoria })
          : t('Sort the {{count}}', { count: total })}
      </Boton>
      <Boton
        bloque
        variante="fantasma"
        isDisabled={colocando}
        onPress={onNo}
        data-testid="mismo-comercio-no"
      >
        {t("No, I'll look at them")}
      </Boton>
    </Tarjeta>
  );
}

type AvisoColocadosProps = {
  n: number;
  onDeshacer: () => void;
};

/** Tras el sí: cuántos se han colocado, que siguen por aprobar, y Deshacer. */
export function AvisoColocados({ n, onDeshacer }: AvisoColocadosProps) {
  const { t } = useTranslation();
  return (
    <View
      data-testid="mismo-comercio-colocados"
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 14,
        backgroundColor: color.accentSoft,
      }}
    >
      <Text style={{ ...texto.secundario, flex: 1, color: color.fg }}>
        {t(
          '{{count}} sorted. They are waiting in To review for your approval.',
          { count: n },
        )}
      </Text>
      <Button
        variant="bare"
        onPress={onDeshacer}
        data-testid="mismo-comercio-deshacer"
        style={{
          minHeight: 44,
          padding: '0 8px',
          ...texto.secundario,
          fontWeight: 800,
          color: color.accent,
        }}
      >
        <Trans>Undo</Trans>
      </Button>
    </View>
  );
}
